import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { TrendingUp, User, LogOut, Settings, BookOpen, Users, Menu, X, Radio, Bot, Calculator, Trophy, MessageSquareQuote, UserPlus, ScanLine, BookOpenCheck } from 'lucide-react';
import { useState } from 'react';

export function Header({ gold = false }) {
    const { user, logout, isAdmin } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    return (
        <header className={`sticky top-0 z-50 ${gold ? 'bg-[#050505]/85 backdrop-blur-md border-b border-zinc-900' : 'nav-header'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center gold-glow" style={{ backgroundColor: gold ? '#FFB800' : 'var(--primary-color)' }}>
                            <TrendingUp className="w-6 h-6" style={{ color: gold ? '#050505' : 'var(--primary-text)' }} />
                        </div>
                        <div className="hidden sm:block">
                            <p className="font-bold text-lg tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                NIKAYOFX
                            </p>
                            <p className="text-xs tracking-widest" style={{ color: 'var(--text-muted)' }}>
                                PTG
                            </p>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    {user && (
                        <nav className="hidden md:flex items-center gap-1">
                            {isAdmin ? (
                                <>
                                    <Link
                                        to="/admin"
                                        className={`nav-link ${isActive('/admin') ? 'active' : ''}`}
                                        data-testid="nav-admin-dashboard"
                                    >
                                        <Settings className="w-4 h-4 inline-block mr-2" />
                                        Dashboard
                                    </Link>
                                    <Link
                                        to="/admin/courses"
                                        className={`nav-link ${isActive('/admin/courses') ? 'active' : ''}`}
                                        data-testid="nav-admin-courses"
                                    >
                                        <BookOpen className="w-4 h-4 inline-block mr-2" />
                                        Courses
                                    </Link>
                                    <Link
                                        to="/admin/students"
                                        className={`nav-link ${isActive('/admin/students') ? 'active' : ''}`}
                                        data-testid="nav-admin-students"
                                    >
                                        <Users className="w-4 h-4 inline-block mr-2" />
                                        Students
                                    </Link>
                                    <Link
                                        to="/admin/signals"
                                        className={`nav-link ${isActive('/admin/signals') ? 'active' : ''}`}
                                        data-testid="nav-admin-signals"
                                    >
                                        <Radio className="w-4 h-4 inline-block mr-2" />
                                        Signals
                                    </Link>
                                    <Link
                                        to="/admin/engine"
                                        className={`nav-link ${isActive('/admin/engine') ? 'active' : ''}`}
                                        data-testid="nav-admin-engine"
                                    >
                                        <Bot className="w-4 h-4 inline-block mr-2" />
                                        Engine
                                    </Link>
                                    <Link
                                        to="/admin/challenges"
                                        className={`nav-link ${isActive('/admin/challenges') ? 'active' : ''}`}
                                        data-testid="nav-admin-challenges"
                                    >
                                        <Trophy className="w-4 h-4 inline-block mr-2" />
                                        Challenge
                                    </Link>
                                    <Link
                                        to="/admin/reviews"
                                        className={`nav-link ${isActive('/admin/reviews') ? 'active' : ''}`}
                                        data-testid="nav-admin-reviews"
                                    >
                                        <MessageSquareQuote className="w-4 h-4 inline-block mr-2" />
                                        Reviews
                                    </Link>
                                    <Link
                                        to="/admin/leads"
                                        className={`nav-link ${isActive('/admin/leads') ? 'active' : ''}`}
                                        data-testid="nav-admin-leads"
                                    >
                                        <UserPlus className="w-4 h-4 inline-block mr-2" />
                                        Leads
                                    </Link>
                                    <Link to="/admin/analyst" className={`nav-link ${isActive('/admin/analyst') ? 'active' : ''}`} data-testid="nav-admin-analyst">
                                        <ScanLine className="w-4 h-4 inline-block mr-2" />
                                        Analyst
                                    </Link>
                                    <Link to="/admin/case-studies" className={`nav-link ${isActive('/admin/case-studies') ? 'active' : ''}`} data-testid="nav-admin-case-studies">
                                        <BookOpenCheck className="w-4 h-4 inline-block mr-2" />
                                        Case Studies
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/dashboard"
                                        className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                                        data-testid="nav-dashboard"
                                    >
                                        {t('nav.dashboard')}
                                    </Link>
                                    <Link
                                        to="/courses"
                                        className={`nav-link ${isActive('/courses') ? 'active' : ''}`}
                                        data-testid="nav-courses"
                                    >
                                        {t('nav.courses')}
                                    </Link>
                                    <Link
                                        to="/signals"
                                        className={`nav-link ${isActive('/signals') ? 'active' : ''}`}
                                        data-testid="nav-signals"
                                    >
                                        <Radio className="w-4 h-4 inline-block mr-2" />
                                        {t('nav.signals')}
                                    </Link>
                                    <Link
                                        to="/tools/risk-calculator"
                                        className={`nav-link ${isActive('/tools/risk-calculator') ? 'active' : ''}`}
                                        data-testid="nav-risk-calculator"
                                    >
                                        <Calculator className="w-4 h-4 inline-block mr-2" />
                                        Risk Calc
                                    </Link>
                                    <Link
                                        to="/assistant"
                                        className={`nav-link ${isActive('/assistant') ? 'active' : ''}`}
                                        data-testid="nav-assistant"
                                    >
                                        <Bot className="w-4 h-4 inline-block mr-2" />
                                        Assistant
                                    </Link>
                                    <Link
                                        to="/challenge"
                                        className={`nav-link ${isActive('/challenge') ? 'active' : ''}`}
                                        data-testid="nav-challenge"
                                    >
                                        <Trophy className="w-4 h-4 inline-block mr-2" style={{ color: '#FFB800' }} />
                                        {t('challenge.navLabel')}
                                    </Link>
                                </>
                            )}
                        </nav>
                    )}

                    {/* Right side */}
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher compact />
                        {user ? (
                            <>
                                {/* User Menu */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="flex items-center gap-2 px-3"
                                            data-testid="user-menu-trigger"
                                        >
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-muted)' }}>
                                                <User className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                                            </div>
                                            <span className="hidden sm:inline text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                                {user.name}
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-default)' }}>
                                        <div className="px-3 py-2">
                                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
                                        </div>
                                        <DropdownMenuSeparator style={{ backgroundColor: 'var(--border-subtle)' }} />
                                        {user.role === 'student' && user.current_phase && (
                                            <>
                                                <div className="px-3 py-2">
                                                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('nav.currentPhase')}</p>
                                                    <p className="text-sm font-medium" style={{ color: 'var(--primary-color)' }}>
                                                        {t(`phases.${user.current_phase}.name`)}
                                                    </p>
                                                </div>
                                                <DropdownMenuSeparator style={{ backgroundColor: 'var(--border-subtle)' }} />
                                            </>
                                        )}
                                        <DropdownMenuItem
                                            onClick={handleLogout}
                                            className="cursor-pointer"
                                            data-testid="logout-button"
                                        >
                                            <LogOut className="w-4 h-4 mr-2" />
                                            {t('common.signOut')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                {/* Mobile menu button */}
                                <button
                                    className="md:hidden p-2"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    data-testid="mobile-menu-toggle"
                                >
                                    {mobileMenuOpen ? (
                                        <X className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                                    ) : (
                                        <Menu className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
                                    )}
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Link to="/login">
                                    <Button variant="ghost" className="btn-ghost" data-testid="login-nav-btn">
                                        {t('nav.login')}
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button className={gold ? 'rounded-full bg-[#FFB800] text-black font-bold text-sm hover:bg-[#FFC933]' : 'btn-primary text-sm'} data-testid="register-nav-btn">
                                        {t('nav.enrollNow')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && user && (
                    <div className="md:hidden py-4 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                        <nav className="flex flex-col gap-1">
                            {isAdmin ? (
                                <>
                                    <Link
                                        to="/admin"
                                        className={`nav-link ${isActive('/admin') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Settings className="w-4 h-4 inline-block mr-2" />
                                        Dashboard
                                    </Link>
                                    <Link
                                        to="/admin/courses"
                                        className={`nav-link ${isActive('/admin/courses') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <BookOpen className="w-4 h-4 inline-block mr-2" />
                                        Courses
                                    </Link>
                                    <Link
                                        to="/admin/students"
                                        className={`nav-link ${isActive('/admin/students') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Users className="w-4 h-4 inline-block mr-2" />
                                        Students
                                    </Link>
                                    <Link
                                        to="/admin/signals"
                                        className={`nav-link ${isActive('/admin/signals') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Radio className="w-4 h-4 inline-block mr-2" />
                                        Signals
                                    </Link>
                                    <Link
                                        to="/admin/engine"
                                        className={`nav-link ${isActive('/admin/engine') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Bot className="w-4 h-4 inline-block mr-2" />
                                        Engine
                                    </Link>
                                    <Link
                                        to="/admin/challenges"
                                        className={`nav-link ${isActive('/admin/challenges') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Trophy className="w-4 h-4 inline-block mr-2" />
                                        Challenge
                                    </Link>
                                    <Link
                                        to="/admin/reviews"
                                        className={`nav-link ${isActive('/admin/reviews') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <MessageSquareQuote className="w-4 h-4 inline-block mr-2" />
                                        Reviews
                                    </Link>
                                    <Link
                                        to="/admin/leads"
                                        className={`nav-link ${isActive('/admin/leads') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <UserPlus className="w-4 h-4 inline-block mr-2" />
                                        Leads
                                    </Link>
                                    <Link to="/admin/analyst" className={`nav-link ${isActive('/admin/analyst') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                                        <ScanLine className="w-4 h-4 inline-block mr-2" />
                                        Analyst
                                    </Link>
                                    <Link to="/admin/case-studies" className={`nav-link ${isActive('/admin/case-studies') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                                        <BookOpenCheck className="w-4 h-4 inline-block mr-2" />
                                        Case Studies
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/dashboard"
                                        className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('nav.dashboard')}
                                    </Link>
                                    <Link
                                        to="/courses"
                                        className={`nav-link ${isActive('/courses') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('nav.courses')}
                                    </Link>
                                    <Link
                                        to="/signals"
                                        className={`nav-link ${isActive('/signals') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Radio className="w-4 h-4 inline-block mr-2" />
                                        {t('nav.signals')}
                                    </Link>
                                    <Link
                                        to="/tools/risk-calculator"
                                        className={`nav-link ${isActive('/tools/risk-calculator') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Calculator className="w-4 h-4 inline-block mr-2" />
                                        Risk Calc
                                    </Link>
                                    <Link
                                        to="/assistant"
                                        className={`nav-link ${isActive('/assistant') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Bot className="w-4 h-4 inline-block mr-2" />
                                        Assistant
                                    </Link>
                                    <Link
                                        to="/challenge"
                                        className={`nav-link ${isActive('/challenge') ? 'active' : ''}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Trophy className="w-4 h-4 inline-block mr-2" style={{ color: '#FFB800' }} />
                                        {t('challenge.navLabel')}
                                    </Link>
                                </>
                            )}
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
